import type { TrendsGraphPoint } from "../services/TrendsService";
import type { GoogleHealthDayRow, GoogleHealthPayload } from "../types/googleHealthBridge.types";
import type { TrendsPageViewModel } from "./trendsPageMappers";
import { readApiDistance } from "./fitnessMetrics";

const WEEK_DAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"] as const;

const num = (v: unknown, fallback = 0): number => {
  if (v == null) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const formatSleepHours = (minutes: number): string => {
  if (minutes <= 0) return "0h 0 min";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m} min`;
};

const dateToShortDay = (dateStr: string, index: number): string => {
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return WEEK_DAYS[index] ?? `${index}`;
  return d.toLocaleDateString("en-US", { weekday: "short" }).slice(0, 2);
};

const pickRowForDate = (
  rows: GoogleHealthDayRow[],
  date: string,
): GoogleHealthDayRow | undefined => rows.find((r) => r.date === date);

const toLocalDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const emptyHealthRow = (date: string): GoogleHealthDayRow => ({
  date,
  steps: 0,
  calories: 0,
  distance: 0,
  sleep_minutes: 0,
  spo2: null,
  hrv: null,
  breathing_rate: null,
  skin_temperature: null,
});

/** Always end the 7-day window on `endDate` so today/Sunday is the last column. */
const lastSevenRowsEndingOn = (
  rows: GoogleHealthDayRow[],
  endDate: string,
): GoogleHealthDayRow[] => {
  const byDate = new Map(rows.map((row) => [row.date, row]));
  const end = new Date(`${endDate}T12:00:00`);
  if (Number.isNaN(end.getTime())) {
    const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date));
    return sorted.slice(-7);
  }

  const out: GoogleHealthDayRow[] = [];
  for (let i = 6; i >= 0; i -= 1) {
    const d = new Date(end);
    d.setDate(end.getDate() - i);
    const key = toLocalDateKey(d);
    out.push(byDate.get(key) ?? emptyHealthRow(key));
  }
  return out;
};

const weeklyAvg = (graph: TrendsGraphPoint[]): number => {
  const withValues = graph.filter((d) => d.value > 0);
  if (!withValues.length) return 0;
  return Math.round(withValues.reduce((s, d) => s + d.value, 0) / withValues.length);
};

const buildBarData = (
  rows: GoogleHealthDayRow[],
  pickValue: (row: GoogleHealthDayRow) => number,
): TrendsGraphPoint[] =>
  rows.map((row, i) => ({
    day: dateToShortDay(row.date, i),
    value: pickValue(row),
  }));

const mergeDailyFromHealth = (
  base: TrendsPageViewModel["daily"],
  row?: GoogleHealthDayRow,
): TrendsPageViewModel["daily"] => {
  if (!row) return base;

  const sleepMinutes = row.sleep_minutes > 0 ? row.sleep_minutes : base.sleep_minutes;

  const fromBridge = readApiDistance(row as unknown as Record<string, unknown>);

  return {
    ...base,
    steps: row.steps > 0 ? row.steps : base.steps,
    calories_burned: row.calories > 0 ? row.calories : base.calories_burned,
    distance_m:
      fromBridge != null && fromBridge > 0 ? fromBridge : base.distance_m,
    sleep_minutes: sleepMinutes,
    sleep_hours: sleepMinutes > 0 ? formatSleepHours(sleepMinutes) : base.sleep_hours,
    hrv_rmssd: row.hrv != null ? num(row.hrv) : base.hrv_rmssd,
    spo2_percent: row.spo2 != null ? num(row.spo2) : base.spo2_percent,
    breathing_rate: row.breathing_rate != null ? num(row.breathing_rate) : base.breathing_rate,
    skin_temp_celsius:
      row.skin_temperature != null ? num(row.skin_temperature) : base.skin_temp_celsius,
  };
};

/** Overlay Google Health bridge data onto the trends view model (fitness fields only). */
export const mergeGoogleHealthIntoTrendsViewModel = (
  base: TrendsPageViewModel,
  health: GoogleHealthPayload | null | undefined,
  selectedDate: string,
): TrendsPageViewModel => {
  if (!health?.health_data?.length) return base;

  const weekRows = lastSevenRowsEndingOn(health.health_data, selectedDate);
  const todayRow = pickRowForDate(health.health_data, selectedDate);

  const stepsBarData = buildBarData(weekRows, (r) => num(r.steps));
  const calBarData = buildBarData(weekRows, (r) => num(r.calories));
  const sleepBarData = buildBarData(weekRows, (r) =>
    r.sleep_minutes > 0 ? r.sleep_minutes / 60 : 0,
  );
  const hrvLineData = weekRows.map((r) => (r.hrv != null ? num(r.hrv) : 0));

  const daily = mergeDailyFromHealth(base.daily, todayRow);

  return {
    ...base,
    daily,
    stepsBarData,
    calBarData,
    sleepBarData,
    hrvLineData,
    weeklyStepAvg: weeklyAvg(stepsBarData),
    weeklyStepTotal: Math.round(stepsBarData.reduce((sum, d) => sum + d.value, 0)),
    weeklyCalAvg: weeklyAvg(calBarData),
  };
};
